import React, { useState } from "react"; // This is actually used. It's just not detected by the linter
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  TouchableOpacity,
  Text,
  PixelRatio,
} from "react-native";
import variables from "../../styles/variables";
import { ContentListItem } from "../../types/content";
import Icon from "../Icon";
import RText from "../RText";
// @ts-ignore
import VideoPreviewIcon from "./VideoPreviewIcon";
import ContentPreviewIcon from "./ContentPreviewIcon";
import { LoadingState } from "../States/states";
import { useResponsive } from "../../hooks/useResponsive";
import { RTextSize } from "../RText/RText";
import TermsModal from "./TermsModal";
import ShowMore from "../SectionHeader/ShowMore";
import { RootStackParamList } from "../../types/route";
import { DEFAULT_REPLAY_IMAGE } from "../Image/images/defaultImages";
import Gap from "../Gap";
import { isInternalLink } from "../Navigation/URLMapper";
import RCorner from "../RCorner/RCorner";

const { spacing } = variables;
const isNative = Platform.OS !== "web";
const isWeb = Platform.OS === "web";
const pixelRatio = PixelRatio.get();

export type ContentPreviewProps = {
  thumbnailUrl?: string; // use an image as the thumnail
  thumbnailText?: string; // overlay some text on the thumbnail
  data?: any;
  description?: string;
  descriptionIcon?: string;
  descriptionFontSize?: RTextSize;
  displayAs?: "list" | "card" | "free-bet";
  icon?: "infoPink";
  style?: any;
  onPress?: () => void;
  arrowNavigatesTo?: {
    screen: keyof RootStackParamList;
    params?: any;
  };
  video?: boolean;
  handleLayout?: (event: any) => void;
  arrowText?: string;
  descriptionHeading?: string;
  isLivePreview?: boolean; // if true, show the live button on the preview
  capitalize?: boolean;
  externalHref?: string;
  inGrid?: boolean;
  loading?: "lazy" | "eager";
  alt?: string;
  showRCorner?: boolean;
  xSmallOnly?: boolean;
} & ContentListItem;

/**
 * ContentPreview is a small rectangle (usually nested inside a GridLayout or ContentScrollBar) which displays a preview of content, such as an article or video.
 * It includes a thumbnail, optional description, and optional icon.
 *
 * @param {string} thumbnailUrl - The URL of the thumbnail image.
 * @param {string} thumbnailText - Text to overlay on the thumbnail.
 * @param {any} data - Additional data related to the content.
 * @param {string} description - Description text for the content.
 * @param {string} descriptionIcon - Icon name for the description.
 * @param {RTextSize} descriptionFontSize - Font size for the description text.
 * @param {"list" | "card" | "free-bet"} displayAs - How to display the content (list, card, or free-bet).
 * @param {"infoPink"} icon - Icon type.
 * @param {any} style - Custom styles for the parent container.
 * @param {() => void} onPress - Function to call when the component is pressed.
 * @param {{ screen: keyof RootStackParamList; params?: any }} arrowNavigatesTo - Navigation parameters for arrow click.
 * @param {boolean} video - Whether the content is a video.
 * @param {(event: any) => void} handleLayout - Function to handle layout changes.
 * @param {string} arrowText - Text for the arrow button.
 * @param {string} descriptionHeading - Heading text for the description.
 * @param {boolean} isLivePreview - Whether to show a live preview button.
 * @param {boolean} capitalize - Whether to capitalize the heading text.
 * @param {string} externalHref - External link URL if applicable.
 * @param {boolean} inGrid - Whether to display in a grid layout.
 * @param {"lazy" | "eager"} loading - Loading strategy for images (lazy or eager).
 * @param {string} alt - Alt text for the thumbnail image.
 * @param {boolean} showRCorner - Whether to show a corner indicator.
 * @param {boolean} xSmallOnly - Whether to restrict width on small screens.
 */

const ContentPreview: React.FC<ContentPreviewProps> = ({
  thumbnailUrl,
  thumbnailText,
  data,
  description,
  descriptionIcon,
  descriptionFontSize = "sm",
  displayAs = "card",
  icon,
  style,
  onPress,
  arrowNavigatesTo,
  video = false,
  isLoading,
  handleLayout,
  arrowText,
  descriptionHeading,
  isLivePreview = false,
  capitalize = true,
  externalHref = null,
  inGrid = false,
  loading = "lazy",
  alt = undefined,
  showRCorner = false,
  xSmallOnly = false,
}) => {
  const { isLargeAndUp, isMediumAndDown, isSmallAndDown } = useResponsive();
  const [openModal, setOpenModal] = useState(false);
  const displayAsList = displayAs === "list";
  const hoverRef = React.useRef(null);

  if (isLoading) return <LoadingState name="ContentPreviewLoader" />;

  const LinkWrapper = ({ children }) => {
    if (Platform.OS == "web" && !!externalHref) {
      return (
        <a
          href={externalHref}
          target={isInternalLink(externalHref) ? "_self" : "_blank"}
          style={{
            textDecoration: "none",
            ...(inGrid ? { minHeight: 200, flexBasis: 300, flexGrow: 1 } : {}),
          }}
        >
          {children}
        </a>
      );
    } else {
      return <>{children}</>;
    }
  };

  const handleMouseEnter = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.backgroundColor = variables.colors.hovered; // Example hover style
    }
  };

  const handleMouseLeave = () => {
    if (hoverRef.current && Platform.OS === "web") {
      hoverRef.current.style.backgroundColor = "white"; // Revert style
    }
  };

  return (
    <>
      <LinkWrapper>
        <TouchableOpacity
          ref={hoverRef}
          style={[
            styles.container,
            displayAsList && stylesAsList.container,
            style,
          ]} // @ts-ignore
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          // so we must re-fire on the children.
          onLayout={handleLayout}
        >
          <TouchableOpacity
            style={[
              styles.imageContainer,
              displayAsList && {
                ...stylesAsList.imageContainer,
                width: isMediumAndDown ? 128 : 160,
              },
            ]}
            onPress={onPress}
            // @ts-ignore
            onMouseEnter={handleMouseEnter}
          >
            {video ? (
              <VideoPreviewIcon
                thumbnailUrl={thumbnailUrl || DEFAULT_REPLAY_IMAGE}
                onPress={onPress}
                isLive={isLivePreview}
                size="small"
                loading={loading}
                alt={alt}
                inList={displayAsList}
              />
            ) : (
              <ContentPreviewIcon
                thumbnailUrl={thumbnailUrl || DEFAULT_REPLAY_IMAGE} // @ts-ignore
                thumbnailText={thumbnailText}
                size="small"
                loading={loading}
                inList={displayAsList}
                alt={alt}
              />
            )}
          </TouchableOpacity>
          <View
            style={[
              !displayAsList && styles.descriptionContainer,
              displayAsList && stylesAsList.descriptionContainer,
              isLargeAndUp && stylesAsList.descriptionInRow,
              displayAs == "free-bet" && { flexDirection: "row" },
            ]}
          >
            {icon && <Gap size="large" />}
            {openModal && (
              <TermsModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                data={data}
              />
            )}
            <Pressable
              style={{
                flexDirection: "column",
                justifyContent: "center",
                width: icon ? 248 : "100%",
                height: "100%",
              }}
              onPress={onPress}
            >
              <View
                style={{
                  width: "100%",
                  flexDirection: "column",
                  display: "flex",
                  alignSelf: "center",
                  marginTop: 6,
                  marginVertical: 0,
                }}
                hitSlop={styles.hitSlopVertical}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    height: "100%",
                    paddingBottom:
                      Platform.OS == "web" && !displayAsList ? 6 : 0,
                  }}
                >
                  {!!descriptionIcon && (
                    <Icon
                      size="tipTag"
                      name={descriptionIcon}
                      style={{ marginRight: spacing.xxsmall }}
                      fill="palette.neutral.x0"
                    />
                  )}
                  <View
                    style={{
                      flexDirection: displayAs === "list" ? "column" : "row",
                      alignItems:
                        displayAs === "list" ? "flex-start" : "center",
                      flex: 1,
                      height: "100%",
                      maxWidth: icon && xSmallOnly ? "65%" : undefined,
                    }}
                  >
                    {!!descriptionHeading && (
                      <RText
                        weight="bold"
                        numberOfLines={displayAsList ? 3 : 2}
                        style={{
                          marginRight: description
                            ? variables.spacing.xsmall
                            : null,
                          minWidth: 35,
                        }}
                        size={isMediumAndDown ? "sm" : "md"}
                        lineHeight={isMediumAndDown ? 18 : 20}
                      >
                        {capitalize
                          ? descriptionHeading.capitalize()
                          : descriptionHeading}
                      </RText>
                    )}
                    <RText
                      numberOfLines={displayAsList ? 3 : 2}
                      style={styles.descriptionText}
                    >
                      {description}
                    </RText>
                  </View>
                </View>
              </View>
            </Pressable>
          </View>
          {showRCorner ? (
            <RCorner
              key={`corner-${Math.random()}`}
              styleOverride={{ top: 0 }}
            />
          ) : null}
          {displayAsList && (
            <View style={stylesAsList.iconContainer}>
              <ShowMore
                navigateTo={arrowNavigatesTo?.screen}
                queryParams={arrowNavigatesTo?.params}
                text={arrowText}
                style={isWeb && isSmallAndDown && { flex: 1 }}
                textStyle={{ flex: 1 }}
              />
            </View>
          )}
        </TouchableOpacity>
      </LinkWrapper>
      {!!icon && (
        <TouchableOpacity
          style={styles.iconCopy}
          onPress={() => setOpenModal(true)}
          hitSlop={styles.hitSlopSquare}
        >
          <Icon name={icon} size="large" fill="palette.rtv.secondary" />
        </TouchableOpacity>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: variables.colors.palette.neutral.x100,
    padding: 3,
    paddingBottom: 0,
    backgroundColor: variables.colors.palette.neutral.x0,
  },
  imageContainer: {
    aspectRatio: 16 / 9,
    justifyContent: "center",
    alignItems: "center",
  },
  descriptionContainer: {
    height: isNative ? Math.floor(50 * pixelRatio) / pixelRatio : 50,
    width: "100%",
    alignItems: "center",
    padding: 3,
  },
  description: {
    width: "100%",
    justifyContent: "center",
  },
  descriptionText: {
    ...Platform.select({
      web: {
        textWrap: "wrap",
      },
    }),
    textAlign: "left",
    flex: 1,
  },
  icon: {
    marginRight: 5,
    justifyContent: "center",
  },
  iconCopy: {
    marginRight: 5,
    justifyContent: "center",
    position: "absolute",
    bottom: 10,
    left: 5,
  },
  hitSlopSquare: {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10,
  },
  hitSlopVertical: {
    top: 20,
    bottom: 20,
  },
  modal: {
    minHeight: 80,
    marginRight: "auto",
    marginLeft: "auto",
  },
  terms: {
    padding: variables.spacing.xxsmall,
  },
});

const stylesAsList = StyleSheet.create({
  container: {
    flexDirection: "row",
    borderWidth: 0,
    borderBottomWidth: 1,
    width: "100%",
    height: isNative ? Math.floor(82 * pixelRatio) / pixelRatio : undefined,
    borderRadius: 0,
    paddingBottom: variables.spacing.xxsmall,
    alignItems: "center",
  },
  imageContainer: {
    aspectRatio: 16 / 9,
  },
  descriptionContainer: {
    display: "flex",
    justifyContent: "center",
    flex: 2,
    marginLeft: variables.spacing.xsmall,
    marginTop: 0,
  },
  iconContainer: {
    height: "100%",
    justifyContent: "flex-end",
    alignItems: "center",
    flex: 1,
    display: "flex",
    flexDirection: "row",
  },
  descriptionInRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
});

export default ContentPreview;
